import React, { useEffect, useMemo, useState } from 'react';
import { CheckOutlined } from '@ant-design/icons';
import { Button, Drawer, Flex, Input, List, Space, Typography } from 'antd';
import { toSearchText } from 'utils/text';

const { Text } = Typography;

const ProductPickerMobile = ({ viewModel }) => {
  const {
    value,
    onChange,
    mode,
    placeholder,
    selectProps,
    options,
    loading,
  } = viewModel;

  const [open, setOpen] = useState(false);
  const [keywordInput, setKeywordInput] = useState('');
  const [keyword, setKeyword] = useState('');
  const [draftValue, setDraftValue] = useState(value);

  const isMultiple = mode === 'multiple' || mode === 'tags';

  useEffect(() => {
    const timeout = setTimeout(() => {
      setKeyword(keywordInput);
    }, 200);

    return () => clearTimeout(timeout);
  }, [keywordInput]);

  const normalizedValue = useMemo(() => {
    if (isMultiple) {
      return Array.isArray(value) ? value : value ? [value] : [];
    }

    return value ?? null;
  }, [isMultiple, value]);

  const normalizedOptions = useMemo(() => {
    return (options || []).map((item) => ({
      ...item,
      __searchLabel: item.searchText || toSearchText(String(item.label || '')),
    }));
  }, [options]);

  const filteredOptions = useMemo(() => {
    const normalizedKeyword = toSearchText(keyword.trim());

    if (!normalizedKeyword) {
      return normalizedOptions;
    }

    return normalizedOptions.filter((item) => item.__searchLabel.includes(normalizedKeyword));
  }, [keyword, normalizedOptions]);

  const optionLabelMap = useMemo(() => {
    return new Map((options || []).map((item) => [item.value, item.label]));
  }, [options]);

  const selectedLabel = useMemo(() => {
    if (isMultiple) {
      const selectedValues = Array.isArray(normalizedValue) ? normalizedValue : [];

      if (selectedValues.length === 0) {
        return placeholder;
      }

      const selectedLabels = selectedValues
        .map((valueItem) => optionLabelMap.get(valueItem))
        .filter(Boolean);

      if (selectedLabels.length <= 2) {
        return selectedLabels.join(', ');
      }

      return `Đã chọn ${selectedLabels.length} sản phẩm`;
    }

    if (!normalizedValue) {
      return placeholder;
    }

    return optionLabelMap.get(normalizedValue) || placeholder;
  }, [isMultiple, normalizedValue, optionLabelMap, placeholder]);

  const selectedValueSet = useMemo(() => {
    return new Set(Array.isArray(draftValue) ? draftValue : []);
  }, [draftValue]);

  const onOpen = () => {
    setDraftValue(normalizedValue);
    setKeywordInput('');
    setKeyword('');
    setOpen(true);
  };

  const onClose = () => {
    setOpen(false);
    setKeywordInput('');
    setKeyword('');
  };

  const onToggleOption = (optionValue) => {
    if (!isMultiple) {
      setDraftValue(optionValue);
      return;
    }

    setDraftValue((prevValue) => {
      const currentValues = Array.isArray(prevValue) ? prevValue : [];
      if (currentValues.includes(optionValue)) {
        return currentValues.filter((item) => item !== optionValue);
      }

      return [...currentValues, optionValue];
    });
  };

  const onClear = () => {
    const nextValue = isMultiple ? [] : undefined;
    setDraftValue(nextValue);
  };

  const onApply = () => {
    if (onChange) {
      onChange(draftValue);
    }
    onClose();
  };

  const isSelected = (optionValue) => (isMultiple ? selectedValueSet.has(optionValue) : draftValue === optionValue);

  return (
    <>
      <Button block onClick={onOpen} disabled={selectProps.disabled} loading={loading} style={{ textAlign: 'left', overflow: 'hidden' }}>
        <span style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {selectedLabel}
        </span>
      </Button>

      <Drawer title={placeholder} placement='bottom' open={open} onClose={onClose} height='80vh'>
        <Space direction='vertical' size={12} style={{ width: '100%' }}>
          <Input
            value={keywordInput}
            onChange={(event) => setKeywordInput(event.target.value)}
            placeholder='Tìm sản phẩm'
            allowClear
          />

          <div style={{ width: '100%', maxHeight: '50vh', overflowY: 'auto' }}>
            <List
              dataSource={filteredOptions}
              locale={{ emptyText: <Text type='secondary'>Không có sản phẩm phù hợp</Text> }}
              renderItem={(item) => (
                <List.Item
                  key={item.value}
                  onClick={() => onToggleOption(item.value)}
                  style={{ cursor: 'pointer', padding: '10px 4px' }}
                >
                  <Flex align='center' justify='space-between' gap={12} style={{ width: '100%' }}>
                    <Text style={{ flex: 1, minWidth: 0, textAlign: 'left' }} ellipsis={{ tooltip: item.label }}>
                      {item.label}
                    </Text>
                    {isSelected(item.value) ? <CheckOutlined style={{ color: '#1677ff' }} /> : <span style={{ width: 14 }} />}
                  </Flex>
                </List.Item>
              )}
            />
          </div>

          <Flex gap={8}>
            <Button block onClick={onClear} disabled={isMultiple ? !(draftValue || []).length : !draftValue}>
              Xoá
            </Button>
            <Button type='primary' block onClick={onApply}>
              Xong
            </Button>
          </Flex>
        </Space>
      </Drawer>
    </>
  );
};

export default ProductPickerMobile;